import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { Plus, Users, Pencil, Trash2, ExternalLink, Sparkles, Star, AlertTriangle, ArrowUp, ShieldOff, MessageSquare, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import PlanBadge from "../components/PlanBadge";

const TIERS = [
  { value: "free", label: "Standard", price: 0 },
  { value: "featured", label: "Featured", price: 10 },
  { value: "premium", label: "Premium Spotlight", price: 20 },
];

export default function RecruiterDashboard() {
  const { user, refresh } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgradeJob, setUpgradeJob] = useState(null);
  const [upgradeTier, setUpgradeTier] = useState("featured");
  const [upgradeError, setUpgradeError] = useState("");
  const [subOpen, setSubOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewMsg, setReviewMsg] = useState("");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [boostInfo, setBoostInfo] = useState("");
  const [boostRedirecting, setBoostRedirecting] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRedirecting, setBulkRedirecting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const boostPolledRef = useRef(false);
  const bulkPolledRef = useRef(false);

  const pending = user && user.role === "recruiter" && !user.is_approved;
  const denied = user && user.is_denied;
  const limited = user && user.is_limited;

  const load = async () => {
    setLoading(true);
    try {
      const reqs = [
        api.get("/jobs?mine=true").catch(() => ({ data: [] })),
        api.get("/recruiter/limits").catch(() => ({ data: null })),
        api.get("/recruiter/request-review/mine").catch(() => ({ data: [] })),
      ];
      if (!pending) reqs.push(api.get("/applications").catch(() => ({ data: [] })));
      const [j, l, rv, a] = await Promise.all(reqs);
      setJobs(j.data);
      setLimits(l.data);
      setReviews(rv.data || []);
      setApps((a && a.data) || []);
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [pending]);

  const submitReview = async () => {
    if (reviewMsg.length < 20) { alert("Please write at least 20 characters explaining your request."); return; }
    try {
      await api.post("/recruiter/request-review", { message: reviewMsg });
      setReviewMsg("");
      setReviewOpen(false);
      load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const deleteJob = async (id) => {
    if (!window.confirm("Delete this job and all applications?")) return;
    await api.delete(`/jobs/${id}`);
    load();
  };

  const doBulkBoost = async () => {
    setBoostInfo(""); setBulkRedirecting(true);
    try {
      const { data } = await api.post("/recruiter/bulk-boost", { origin_url: window.location.origin });
      if (data.owner_granted) {
        setBulkOpen(false);
        setBoostInfo(`Bulk Boost granted — every published role is Premium until ${new Date(data.active_until).toLocaleDateString()}.`);
        load();
        return;
      }
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      setBoostInfo("No checkout URL returned for Bulk Boost.");
    } catch (e) {
      setBoostInfo(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBulkRedirecting(false); }
  };

  const doUpgrade = async () => {
    setUpgradeError("");
    setBoostRedirecting(true);
    try {
      const { data } = await api.post(`/jobs/${upgradeJob.job_id}/upgrade`, {
        tier: upgradeTier,
        origin_url: window.location.origin,
      });
      // Owner is granted instantly (no Stripe redirect).
      if (data.owner_granted) {
        setUpgradeJob(null);
        load();
        return;
      }
      // Plan free-redemption (paid plans get N free boosts per month) — applied instantly without Stripe.
      if (data.free_redemption) {
        setBoostInfo(`Free boost redeemed from your ${data.plan} plan — ${data.tier} active. ${data.remaining_after === -1 ? "Unlimited" : data.remaining_after} ${data.tier} boost${data.remaining_after === 1 ? "" : "s"} left this month.`);
        setUpgradeJob(null);
        load();
        return;
      }
      if (data.checkout_url) {
        try { sessionStorage.setItem("boost_pending_job", upgradeJob.job_id); } catch (_e) {}
        window.location.href = data.checkout_url;
        return;
      }
      setUpgradeError("No checkout URL returned from server.");
    } catch (e) {
      setUpgradeError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBoostRedirecting(false);
    }
  };

  // Handle return from Stripe boost checkout
  useEffect(() => {
    const sid = searchParams.get("boost_session_id");
    const cancelled = searchParams.get("boost_cancelled");
    const bulkSid = searchParams.get("bulk_boost_session_id");
    const bulkCancelled = searchParams.get("bulk_boost_cancelled");
    if (cancelled) {
      setBoostInfo("Boost checkout cancelled — no charge made, job tier unchanged.");
      setSearchParams({}, { replace: true });
      return;
    }
    if (bulkCancelled) {
      setBoostInfo("Bulk Boost checkout cancelled — no charge made.");
      setSearchParams({}, { replace: true });
      return;
    }
    if (bulkSid && !bulkPolledRef.current) {
      bulkPolledRef.current = true;
      setBoostInfo("Confirming your Bulk Boost payment…");
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const { data } = await api.get(`/recruiter/bulk-boost/status/${encodeURIComponent(bulkSid)}`);
          if (data.status === "completed" && data.payment_status === "paid") {
            setBoostInfo(`Bulk Boost active — every published role is Premium until ${new Date(data.active_until).toLocaleDateString()}.`);
            setSearchParams({}, { replace: true });
            load();
            return;
          }
          if (data.status === "expired" || data.payment_status === "unpaid") {
            setBoostInfo("Bulk Boost checkout didn't complete. No tiers were changed.");
            setSearchParams({}, { replace: true });
            return;
          }
          if (attempts < 8) setTimeout(poll, 1500);
          else { setBoostInfo("Still confirming… please refresh in a minute."); setSearchParams({}, { replace: true }); }
        } catch (e) {
          setBoostInfo(formatApiError(e.response?.data?.detail) || e.message);
          setSearchParams({}, { replace: true });
        }
      };
      poll();
      return;
    }
    if (!sid || boostPolledRef.current) return;
    boostPolledRef.current = true;
    const jobId = (() => { try { return sessionStorage.getItem("boost_pending_job") || ""; } catch (_e) { return ""; } })();
    if (!jobId) {
      setBoostInfo("Boost confirmation: missing job reference. Refresh to check status.");
      setSearchParams({}, { replace: true });
      return;
    }
    setBoostInfo("Confirming your boost payment…");
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/jobs/${jobId}/upgrade/status/${encodeURIComponent(sid)}`);
        if (data.status === "completed" && data.payment_status === "paid") {
          setBoostInfo(`Boost active: ${data.tier} on this role — payment confirmed.`);
          try { sessionStorage.removeItem("boost_pending_job"); } catch (_e) {}
          setSearchParams({}, { replace: true });
          load();
          return;
        }
        if (data.status === "expired" || data.payment_status === "unpaid") {
          setBoostInfo("Boost checkout didn't complete. Job tier was not changed.");
          setSearchParams({}, { replace: true });
          return;
        }
        if (attempts < 8) setTimeout(poll, 1500);
        else { setBoostInfo("Still confirming… please refresh in a minute."); setSearchParams({}, { replace: true }); }
      } catch (e) {
        setBoostInfo(formatApiError(e.response?.data?.detail) || e.message);
        setSearchParams({}, { replace: true });
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const upgradeSub = async (tier, cycle) => {
    try {
      const { data } = await api.post("/recruiter/subscription", { tier, cycle, origin_url: window.location.origin });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setSubOpen(false);
      await refresh();
      load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Recruiter dashboard</div>
          <h1 className="font-display font-black text-5xl mt-3 tracking-tight">Your pipeline.</h1>
        </div>
        {!pending && (
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setBulkOpen(true)} data-testid="bulk-boost-btn" className="btn-ink text-sm">
              <Star className="w-4 h-4 text-[var(--brand-primary)]" /> Bulk Boost · £50/30d
            </button>
            <Link to="/recruiter/jobs/new" data-testid="new-job-button" className="btn-terra">
              <Plus className="w-4 h-4" /> New role
            </Link>
          </div>
        )}
      </div>

      {boostInfo && (
        <div data-testid="boost-info" className="mt-6 border border-[#0F0F0F] bg-[#0F0F0F]/5 p-4 text-sm">
          {boostInfo}
        </div>
      )}

      {pending && !denied && (
        <div data-testid="pending-banner" className="mt-8 border border-[var(--brand-primary)] bg-[var(--brand-primary)]/5 p-6 flex items-start gap-4">
          <AlertTriangle className="w-6 h-6 text-[var(--brand-primary)] flex-shrink-0" />
          <div>
            <div className="font-display font-bold text-xl">Pending owner approval</div>
            <p className="text-sm text-[#3a3a3a] mt-1">
              Your recruiter account is waiting for the platform owner to approve it. You&apos;ll be able to post roles and review applicants once approved.
            </p>
          </div>
        </div>
      )}

      {denied && (
        <div data-testid="denied-banner" className="mt-8 border-2 border-[var(--brand-primary)] bg-[var(--brand-primary)]/10 p-6">
          <div className="flex items-start gap-4">
            <ShieldOff className="w-6 h-6 text-[var(--brand-primary)] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-xl">Recruiter application denied</div>
              <p className="text-sm text-[#3a3a3a] mt-1">
                The owner reviewed your account and declined it. Reason: <strong>{user.denial_reason || "violation of terms"}</strong>.
              </p>
              {reviews.find((r) => r.status === "open") ? (
                <div data-testid="review-pending" className="mt-3 text-sm text-[#5C5C5C]">
                  <MessageSquare className="w-4 h-4 inline mr-1" /> Your review request is pending — we&apos;ll respond as soon as we&apos;ve looked into it.
                </div>
              ) : (
                <button onClick={() => setReviewOpen(true)} data-testid="request-review-btn" className="btn-terra mt-4">
                  <MessageSquare className="w-4 h-4" /> Request a review
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {limited && (
        <div data-testid="limited-banner" className="mt-8 border-2 border-amber-400 bg-amber-50 p-6 flex items-start gap-4">
          <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-display font-bold text-xl">Account limited</div>
            <p className="text-sm text-[#3a3a3a] mt-1">
              You can still log in and browse, but the following actions are blocked: posting jobs, submitting applications. Reason: <strong>{user.limited_reason}</strong>.
            </p>
          </div>
        </div>
      )}

      {reviewOpen && (
        <div className="fixed inset-0 z-50 bg-[#0F0F0F]/60 flex items-center justify-center p-6" onClick={() => setReviewOpen(false)} data-testid="review-modal">
          <div className="bg-[#FDFCF8] border border-[#0F0F0F] p-8 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="label-eyebrow flex items-center gap-1.5"><MessageSquare className="w-3 h-3" /> Request review</div>
            <h3 className="font-display font-black text-2xl mt-2">Tell us why we should reconsider.</h3>
            <p className="text-sm text-[#5C5C5C] mt-2">Original reason: <em>{user.denial_reason}</em></p>
            <textarea data-testid="review-message" required minLength={20} rows={6} value={reviewMsg} onChange={(e) => setReviewMsg(e.target.value)} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2 mt-3" placeholder="Provide context, evidence, links — anything that helps the owner re-evaluate (min 20 chars)." />
            <div className="flex gap-2 mt-4">
              <button onClick={submitReview} data-testid="submit-review" disabled={reviewMsg.length < 20} className="btn-terra flex-1">
                <MessageSquare className="w-4 h-4" /> Submit request
              </button>
              <button onClick={() => setReviewOpen(false)} className="btn-ghost">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {!pending && limits && limits.plan && (
        <div data-testid="plan-banner" className="mt-8 border border-[#0F0F0F] p-5 grid grid-cols-12 gap-4 items-center">
          <div className="col-span-12 md:col-span-7">
            <div className="label-eyebrow flex items-center gap-1.5">
              Current plan
              {Object.keys(limits.account_overrides || {}).length > 0 && (
                <span data-testid="custom-quota-banner-badge" className="inline-flex items-center gap-1 bg-[var(--brand-primary)] text-white text-[9px] uppercase tracking-[0.2em] font-bold px-1.5 py-0.5">
                  <Sparkles className="w-2.5 h-2.5" /> Custom quotas
                </span>
              )}
            </div>
            <div className="font-display font-black text-2xl mt-1 capitalize">
              {limits.plan.label || limits.plan_key}
              {limits.plan_key !== "free" && limits.plan_key !== "enterprise" && (
                <span className="text-sm font-normal text-[#5C5C5C] ml-2">
                  · £{user?.subscription_cycle === "annual" ? limits.plan.annual_price : limits.plan.monthly_price}/mo
                </span>
              )}
            </div>
            <div className="text-sm text-[#5C5C5C] mt-1">
              Active jobs: <strong>{limits.usage.active_jobs}</strong>
              {limits.plan.max_active_jobs !== -1 ? ` / ${limits.plan.max_active_jobs}` : " · unlimited"}
              {" · "}
              {limits.plan.max_applicants_per_job_per_month === -1
                ? "unlimited applicants"
                : `${limits.plan.max_applicants_per_job_per_month} applicants/job/month`}
              {" · "}
              <span data-testid="team-seat-quota">
                {limits.plan.team_members === -1
                  ? "unlimited team seats"
                  : `${limits.plan.team_members} team seats`}
              </span>
            </div>
            {limits.boosts && (
              <div className="text-xs text-[#5C5C5C] mt-1.5" data-testid="boost-allowance">
                Free boosts this month ·{" "}
                <strong data-testid="boost-allowance-featured">
                  Featured {limits.boosts.featured.remaining === -1 ? "unlimited" : `${limits.boosts.featured.remaining}/${limits.boosts.featured.allowance}`}
                </strong>{" · "}
                <strong data-testid="boost-allowance-premium">
                  Premium {limits.boosts.premium.remaining === -1 ? "unlimited" : `${limits.boosts.premium.remaining}/${limits.boosts.premium.allowance}`}
                </strong>
              </div>
            )}
          </div>
          <div className="col-span-12 md:col-span-5 flex md:justify-end gap-2 flex-wrap">
            {user?.role === "recruiter" && limits.plan_key !== "growth" && (
              <button onClick={() => setSubOpen(true)} data-testid="upgrade-plan" className="btn-terra text-sm">
                <Sparkles className="w-4 h-4" /> Upgrade plan
              </button>
            )}
            {user?.role === "recruiter" && (
              <Link to="/billing" data-testid="billing-link" className="btn-ghost text-sm">Manage billing</Link>
            )}
            <Link to="/pricing" className="btn-ghost text-sm">See plans</Link>
          </div>
        </div>
      )}

      {loading && <div className="mt-10 text-[#5C5C5C]">Loading…</div>}

      {!loading && !pending && (
        <div className="grid grid-cols-12 gap-10 mt-10">
          <section className="col-span-12 lg:col-span-7">
            <div className="label-eyebrow mb-3">Your open roles</div>
            <div className="grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
              {jobs.length === 0 && (
                <div data-testid="no-jobs-recruiter" className="bg-[#FDFCF8] p-10 text-center text-[#5C5C5C]">
                  No roles yet. Post your first one.
                </div>
              )}
              {jobs.map((j) => {
                const count = apps.filter((a) => a.job_id === j.job_id).length;
                return (
                  <div key={j.job_id} data-testid={`recruiter-job-${j.job_id}`} className="bg-[#FDFCF8] p-5 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="label-eyebrow">{j.company}</div>
                        {j.tier === "premium" && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold bg-[#0F0F0F] text-[#FDFCF8] px-1.5 py-0.5"><Star className="w-2.5 h-2.5 text-[var(--brand-primary)]" /> Spotlight</span>}
                        {j.tier === "featured" && <span className="text-[10px] uppercase tracking-widest font-semibold border border-[var(--brand-primary)] text-[var(--brand-primary)] px-1.5 py-0.5">Featured</span>}
                        <PlanBadge plan={j.recruiter_plan} />
                      </div>
                      <h3 className="font-display font-bold text-lg mt-1 truncate">{j.title}</h3>
                      <div className="text-xs text-[#5C5C5C] mt-0.5">{j.location} · {count} applicant{count === 1 ? "" : "s"}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {j.tier !== "premium" && user?.role === "recruiter" && (
                        <button onClick={() => { setUpgradeJob(j); setUpgradeTier(j.tier === "featured" ? "premium" : "featured"); setUpgradeError(""); }}
                          data-testid={`upgrade-${j.job_id}`}
                          className="text-xs font-semibold px-2 py-1.5 border border-[#0F0F0F] hover:bg-[#0F0F0F] hover:text-[#FDFCF8] inline-flex items-center gap-1">
                          <Sparkles className="w-3 h-3" /> Boost
                        </button>
                      )}
                      <Link to={`/jobs/${j.job_id}`} className="p-2 hover:text-[var(--brand-primary)]" title="View public"><ExternalLink className="w-4 h-4" /></Link>
                      <Link to={`/recruiter/jobs/${j.job_id}/edit`} data-testid={`edit-job-${j.job_id}`} className="p-2 hover:text-[var(--brand-primary)]"><Pencil className="w-4 h-4" /></Link>
                      <button onClick={() => deleteJob(j.job_id)} data-testid={`delete-job-${j.job_id}`} className="p-2 hover:text-[var(--brand-primary)]"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="col-span-12 lg:col-span-5">
            <div className="label-eyebrow mb-3 flex items-center gap-1.5"><Users className="w-3 h-3" /> Recent applicants</div>
            <div className="grid gap-px bg-[#E2E0D8] border border-[#E2E0D8] max-h-[600px] overflow-auto">
              {apps.length === 0 && (
                <div className="bg-[#FDFCF8] p-8 text-center text-[#5C5C5C]">No applicants yet.</div>
              )}
              {apps.slice(0, 30).map((a) => (
                <Link key={a.application_id} to={`/recruiter/applications/${a.application_id}`}
                  data-testid={`applicant-row-${a.application_id}`}
                  className="bg-[#FDFCF8] p-4 flex items-center justify-between gap-3 hover:bg-[#F3F2EB] transition">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{a.candidate_name || a.candidate_email}</div>
                    <div className="text-xs text-[#5C5C5C] truncate">{a.job_title}</div>
                  </div>
                  <StatusBadge status={a.status} />
                </Link>
              ))}
            </div>
          </section>
        </div>
      )}

      {/* Subscription upgrade modal */}
      {subOpen && (
        <div className="fixed inset-0 z-50 bg-[#0F0F0F]/60 flex items-center justify-center p-6" data-testid="sub-modal">
          <div className="bg-[#FDFCF8] border border-[#0F0F0F] p-8 max-w-lg w-full">
            <div className="label-eyebrow">Upgrade subscription</div>
            <h3 className="font-display font-black text-2xl mt-2">Pick a plan</h3>
            <p className="text-sm text-[#5C5C5C] mt-1">Cancel anytime. Payments are processed securely by Stripe.</p>
            <div className="mt-5 space-y-3">
              {[
                { tier: "starter", name: "Starter", monthly: 12.99, annual: 9.99, desc: "10 jobs · 100 applicants/job/mo · 1 Featured boost/mo" },
                { tier: "growth", name: "Growth", monthly: 29.99, annual: 24.99, desc: "Unlimited jobs · 3 Featured + 1 Spotlight/mo" },
              ].map((p) => (
                <div key={p.tier} className="border border-[#E2E0D8] p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-display font-bold">{p.name}</div>
                      <div className="text-xs text-[#5C5C5C] mt-0.5">{p.desc}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-display font-bold">£{p.monthly}/mo</div>
                      <div className="text-xs text-[#5C5C5C]">£{p.annual}/mo annual</div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => upgradeSub(p.tier, "monthly")} data-testid={`sub-${p.tier}-monthly`} className="btn-ink text-sm flex-1">Monthly</button>
                    <button onClick={() => upgradeSub(p.tier, "annual")} data-testid={`sub-${p.tier}-annual`} className="btn-terra text-sm flex-1">Annual (save)</button>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setSubOpen(false)} className="mt-4 text-sm font-semibold hover:text-[var(--brand-primary)]">Cancel</button>
          </div>
        </div>
      )}

      {/* Bulk Boost modal */}
      {bulkOpen && (
        <div className="fixed inset-0 z-50 bg-[#0F0F0F]/60 flex items-center justify-center p-6" onClick={() => setBulkOpen(false)} data-testid="bulk-boost-modal">
          <div className="bg-[#FDFCF8] border border-[#0F0F0F] p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="label-eyebrow flex items-center gap-1.5"><Star className="w-3 h-3 text-[var(--brand-primary)]" /> Bulk Boost</div>
            <h3 className="font-display font-black text-3xl mt-2 tracking-tight">£50 · 30 days · Premium Spotlight on every role.</h3>
            <p className="text-sm text-[#5C5C5C] mt-3">
              Promotes <strong>every</strong> published role on your company to Premium Spotlight for 30 days.
              Roles posted during the boost window are also covered automatically.
            </p>
            <ul className="mt-4 text-sm space-y-1.5 text-[#3a3a3a]">
              <li>· Premium Spotlight on all current + future published jobs</li>
              <li>· One flat payment — no auto-renewal</li>
              <li>· Cancel anytime from /billing (resets tiers to free at expiry)</li>
            </ul>
            <div className="flex gap-2 mt-6">
              <button onClick={doBulkBoost} disabled={bulkRedirecting} data-testid="confirm-bulk-boost" className="btn-terra flex-1 disabled:opacity-50">
                {bulkRedirecting ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <><Star className="w-4 h-4 text-[#FDFCF8]" /> Pay £50 & boost</>}
              </button>
              <button onClick={() => setBulkOpen(false)} className="btn-ghost">Cancel</button>
            </div>
            <p className="text-xs text-[#5C5C5C] mt-3 italic">Payments are processed securely by Stripe. Tier activates only after payment is confirmed.</p>
          </div>
        </div>
      )}

      {/* Upgrade modal */}
      {upgradeJob && (
        <div className="fixed inset-0 z-50 bg-[#0F0F0F]/60 flex items-center justify-center p-6" data-testid="upgrade-modal">
          <div className="bg-[#FDFCF8] border border-[#0F0F0F] p-8 max-w-md w-full">
            <div className="label-eyebrow">Boost listing</div>
            <h3 className="font-display font-black text-2xl mt-2">{upgradeJob.title}</h3>
            <p className="text-sm text-[#5C5C5C] mt-1">Promote this role above standard listings.</p>
            <div className="mt-6 space-y-3">
              {TIERS.filter((t) => t.value !== "free").map((t) => {
                const cell = limits?.boosts?.[t.value];
                const free = cell && (cell.remaining === -1 || cell.remaining > 0);
                return (
                  <label key={t.value} className={`block border ${upgradeTier === t.value ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/5" : "border-[#E2E0D8]"} p-4 cursor-pointer`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <input type="radio" name="tier" checked={upgradeTier === t.value} onChange={() => setUpgradeTier(t.value)}
                          data-testid={`tier-option-${t.value}`} />
                        <div className="min-w-0">
                          <div className="font-display font-bold flex items-center gap-1.5">
                            {t.value === "premium" && <Star className="w-3.5 h-3.5 text-[var(--brand-primary)]" />} {t.label}
                            {free && (
                              <span data-testid={`free-pill-${t.value}`} className="text-[10px] uppercase tracking-widest font-semibold bg-[#0F0F0F] text-[#FDFCF8] px-1.5 py-0.5">free with plan</span>
                            )}
                          </div>
                          <div className="text-xs text-[#5C5C5C]">{t.value === "premium" ? "Top of every listing + Spotlight badge" : "Highlighted above standard roles"}</div>
                          {cell && cell.allowance > 0 && (
                            <div className="text-[11px] text-[#5C5C5C] mt-1">
                              {cell.remaining === -1
                                ? "Unlimited with your plan"
                                : `${cell.remaining}/${cell.allowance} free this month`}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="font-display font-bold text-xl flex-shrink-0">
                        {free ? <span className="text-[var(--brand-primary)]">FREE</span> : <>£{t.price}</>}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-[#5C5C5C] mt-4 italic">
              {(() => {
                const cell = limits?.boosts?.[upgradeTier];
                const free = cell && (cell.remaining === -1 || cell.remaining > 0);
                return free
                  ? "Free with your current plan — boost is activated instantly, no card needed."
                  : "Payments are processed securely by Stripe. The boost is only activated after payment is confirmed.";
              })()}
            </p>
            {upgradeError && <div data-testid="boost-error" className="text-sm text-[var(--brand-primary)] mt-2">{upgradeError}</div>}
            <div className="flex gap-2 mt-6">
              <button onClick={doUpgrade} disabled={boostRedirecting} data-testid="confirm-upgrade" className="btn-terra flex-1 disabled:opacity-50">
                {(() => {
                  if (boostRedirecting) return <><Loader2 className="w-4 h-4 animate-spin" /> Working…</>;
                  const cell = limits?.boosts?.[upgradeTier];
                  const free = cell && (cell.remaining === -1 || cell.remaining > 0);
                  return free
                    ? <><ArrowUp className="w-4 h-4" /> Redeem free boost</>
                    : <><ArrowUp className="w-4 h-4" /> Pay £{TIERS.find((t) => t.value === upgradeTier)?.price} & boost</>;
                })()}
              </button>
              <button onClick={() => setUpgradeJob(null)} className="btn-ghost">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
